/**
 * Ink-accurate text (owner decision 12, pixel reconstruction). The reference's text is described by its measured INK box
 * (tight bounds of the glyph pixels) and the words that produced it. This component reproduces the reference typography for
 * any runtime text: the font size is calibrated so the reference words would fill the reference ink width in the current
 * CSS font, the baseline is derived from the reference words' ascent, and the runtime text is laid on that baseline, left- or
 * centre-aligned to the reference ink.
 *
 * HTML/CSS, NOT SVG (00-VISUAL-STYLE-LOCK.md §6, 13.9.2026): "SVG is prohibited in the SayNoWay consumer game UI. 100%
 * ban." This component used to draw an inline <svg><text>, and because Pill and Seg render their labels through it, that
 * one element was the entire SVG surface of the game — every button label on every screen. It now lays a <span> on the
 * same computed baseline. SVG's `y` is the baseline and CSS's is not, so the baseline is placed explicitly: with
 * `line-height: 1` the line box is `px` tall and the baseline sits half-leading + font-ascent below its top, both of
 * which the font itself reports through `fontBoundingBox*`. No guesswork, and nothing else about the layout moves.
 */

/** x,y,w,h = tight ink bounds in stage px; `cap` = cap height of the first capital (y is then the cap top); text = the reference words */
/** rw: the preview's own ink width for `text` — the size is taken from it even when the box is widened for longer live text
 *  (Marko 15.9.2026: fonts at the previews' size, "much smaller"; no hand-set caps) */
export type InkBox = { x: number; y: number; w: number; h: number; cap?: number; text: string; rw?: number };
export type InkProps = {
  /** measured reference ink box + the words that produced it */
  box: InkBox;
  /** runtime text (defaults to the reference words) */
  text?: string;
  class?: string;
  /** stage-space origin of the positioned parent (nested inside an <Art>) */
  parent?: { x: number; y: number };
  align?: 'left' | 'center';
  /** widest allowed ink before glyph compression (default: the reference ink width) */
  maxW?: number;
  /** cut the string and end it with … instead of bringing the size down — for a lane whose size is fixed by its design
   *  (the Public Play row: Codex 21.9.2026, "ei fontin kutistusta; ylittävä nimi katkaistaan ellipsillä") */
  ellipsis?: boolean;
  /** explicit font size in stage px (skips width calibration) */
  size?: number;
  /** calibrate the size from another region's reference words (one size for a class of siblings, e.g. the three tile titles) */
  sizeFrom?: InkBox;
  /** gradient fill: [top, bottom], or measured stops [[offset 0-1, colour], …] for a metal that turns over the letter
   *  height. Painted with background-clip: text, so a runtime string carries the reference's material without baking
   *  the word into a plate. */
  gradient?: [string, string] | [number, string][];
  /** a dark keyline painted UNDER the light stroke: display lettering needs two strokes, so the same string is drawn
   *  twice — once heavy and dark, once as the measured metal (Codex, Home titles 12.9.2026) */
  outline?: { color: string; width: number };
  /** italic slant in degrees, applied about the baseline — the drawn lettering it replaces leaned right */
  skewX?: number;
  /** the smallest font (stage px) this text may shrink to; default 40/0.72 = 16 CSS px at 390 wide. The approved
   *  previews draw the roster name pills at 12 px and the handoff accepts 8-character names in them, so those pass
   *  their pill's own floor instead of overflowing it (production capture, 15.9.2026). */
  floor?: number;
  /** stacking order in the stage (default 60: text sits above surfaces that carry their own z-index) */
  z?: number;
  testid?: string;
};

const canvas = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;
type M = { w: number; adv: number; asc: number; desc: number; left: number; fbAsc: number; fbDesc: number };
function measure(font: string, text: string): M {
  if (!canvas) return { w: 0, adv: 0, asc: 0, desc: 0, left: 0, fbAsc: 0, fbDesc: 0 };
  canvas.font = font; const m = canvas.measureText(text);
  // fontBoundingBox* are the FACE's metrics (same for every string) — they place the baseline inside the line box.
  // actualBoundingBox* are this string's ink — they size and align it. The two are not interchangeable.
  return { w: m.actualBoundingBoxLeft + m.actualBoundingBoxRight, adv: m.width, asc: m.actualBoundingBoxAscent, desc: m.actualBoundingBoxDescent, left: m.actualBoundingBoxLeft,
           fbAsc: m.fontBoundingBoxAscent ?? m.actualBoundingBoxAscent, fbDesc: m.fontBoundingBoxDescent ?? m.actualBoundingBoxDescent };
}

/**
 * Widest ink of a string in stage units, measured in the font that will actually draw it. Exported because a layout
 * decision ("does this label fit a chip, or does it need a full-width row?") must be a measurement, not a character
 * count — PREMIUM_GATE rule 4. Re-measure on `useFontsReady()`: before the web font lands the fallback answers.
 */
export function textInkWidth(text: string, px: number, weight = 800, family = '--font-ui'): number {
  const fam = family.startsWith('--')
    ? (typeof document === 'undefined' ? '' : getComputedStyle(document.documentElement).getPropertyValue(family).trim())
    : family;
  const m = measure(`${weight} ${px}px ${fam || 'sans-serif'}`, text);
  // The ADVANCE, not the ink extents: a layout decision is checked against the box the browser gives the drawn text,
  // and that box carries the side bearings. On a single digit the ink is 6 units narrower than the box, so measuring
  // ink made five chips report an overflow they did not have.
  return Math.max(m.adv, m.w);
}
/**
 * Cap height per px of font size, in the face that will draw it. `Ink` derives its size from the reference cap the same
 * way; a hard-coded 0.72 was close enough to look right and far enough off that a chip's label was measured 30 units
 * narrower than it drew. Read the ratio, do not assume it.
 */
export function textCapRatio(weight = 800, family = '--font-ui'): number {
  const fam = family.startsWith('--')
    ? (typeof document === 'undefined' ? '' : getComputedStyle(document.documentElement).getPropertyValue(family).trim())
    : family;
  const asc = measure(`${weight} 100px ${fam || 'sans-serif'}`, 'H').asc;
  return asc > 0 ? asc / 100 : 0.72;
}
/** kept for two callers (Login, Seg) until their own measuring goes: nothing re-measures any more (OD-036) */
export function useFontsReady(): number { return 0; }

/**
 * ONE TYPE SYSTEM (Marko 15.9.2026: "all the way everywhere the same font, same sizes… that's how designs are made").
 * Every live text after 01–06 belongs to one role; the role fixes family, weight, size and tracking. Sizes are CSS tokens
 * (--ts-<role> in tokens.css, stage px) taken from the median text heights of the approved previews, and the in-game
 * tuner (?type=1) moves them live. 01–06 keep their own classes (lg-*, ui4-*, Home) and are not touched.
 */
export type TypeRole = 'title' | 'region' | 'label' | 'button' | 'chip' | 'name' | 'body' | 'caption' | 'tag' | 'tablehead' | 'number' | 'code' | 'timer' | 'eyebrow' | 'action' | 'hero' | 'command' | 'tab' | 'filter' | 'section' | 'detail' | 'status';
const ROLE_PREFIX = /^(st|nf|lb|e|bk|cr|r|fr|iv|sh|rp|pp|gt|fe|cw|sp|rs|rr|pl|jn|ht)-t-([a-z0-9]+)$/;
const SUFFIX_ROLE: Record<string, TypeRole> = {
  caption: 'caption', title: 'title', head: 'label', heading: 'label', label: 'label', series: 'label', httitle: 'label', howto: 'label', window: 'label', need: 'label',
  btn: 'button', pill: 'button', done: 'button', send: 'button', signout: 'button', lounge: 'button', copy: 'button', cta: 'button', join: 'button', start: 'button', link: 'button', delete: 'button',
  chip: 'chip', tab: 'chip',
  name: 'name', row: 'name', music: 'name',
  sub: 'caption', body: 'caption', htbody: 'caption', howtoline: 'caption', hint: 'caption', note: 'caption', time: 'caption', helper: 'caption', footer: 'caption', meta: 'caption',
  waiting: 'caption', url: 'caption', open: 'caption', value: 'caption', reason: 'caption', artist: 'caption', msg: 'caption', benefit: 'body', pricesub: 'caption', ctasub: 'caption', joinsub: 'caption', scan: 'caption',
  tag: 'tag', status: 'tag', badge: 'tag',
  number: 'number', code: 'code', letter: 'code', balance: 'number', credits: 'number', total: 'number', place: 'number', num: 'number', timer: 'caption', price: 'label', brand: 'title', opt: 'chip', type: 'chip', cardname: 'name', back: 'button',
};
/** nf-t-title is a list row's name, not a panel title */
const ROLE_OVERRIDE: Record<string, TypeRole> = {
  // Condensed commands share a face across lists; prices and broad dialog actions stay rounded.
  'bk-t-btn': 'command', 'nf-t-btn': 'command', 'e-t-btn': 'command', 'r-t-btn': 'command',
  'sh-t-pill': 'command', 'sh-t-cta': 'command',   /* 57: SHARE is the same kind of button as SAVE / COPY LINK under it */ 'fr-t-tab': 'tab', 'pl-t-chip': 'filter',
  'ht-t-head': 'section', 'r-t-price': 'section',
  'ht-t-body': 'detail', 'pl-t-sub': 'detail', 'r-t-pricesub': 'detail',
  'lb-t-status': 'status', 'lb-t-waiting': 'status', 'r-t-status': 'status',
  'cr-t-price': 'button', 'pl-t-link': 'body',

  'nf-t-title': 'name', 'rr-t-title': 'eyebrow', 'e-t-name': 'tag', 'lb-t-name': 'chip', 'lb-t-open': 'chip',
  'e-t-head': 'tablehead', 'e-t-series': 'eyebrow', 'lb-t-heading': 'eyebrow', 'r-t-head': 'eyebrow',
  'rr-t-num': 'timer', 'rr-t-sub': 'label', 'r-t-need': 'body', 'rp-t-reason': 'body', 'st-t-value': 'body',
  'st-t-row': 'label', 'rs-t-row': 'label', 'gt-t-row': 'body', 'e-t-msg': 'command',   /* MESSAGE CREW in the condensed face (Codex 24.9.2026) */ 'jn-t-letter': 'number',
  'iv-t-link': 'button', 'pp-t-link': 'button', 'st-t-delete': 'button', 'jn-t-scan': 'button',
  'bd-round': 'chip', 'bd-name': 'tag', 'bd-tlabel': 'tag', 'bd-default': 'tag',
  'bd-allsix': 'caption', 'bd-callhint': 'caption', 'bd-hint': 'caption',
  'bd-marker-text': 'tag', 'bd-status-sub': 'caption', 'bd-score-sub': 'body',
  'bd-score-pts': 'action', 'bd-continue': 'button', 'rr-next': 'button', 'rr-sub': 'body',
  'rr-t-done': 'hero', 'e-t-winner': 'title',   /* ROUND n DONE and the winner line: the game's own face (owner 22.9.2026) */
  'lb-t-action': 'action', 'lb-t-rule': 'section',   /* the lobby kit (OD-034): the gold CTA's brush word; the start rule — one sentence on two lines, both at the condensed 36 so neither fits down */
};
export function roleOf(cls: string): TypeRole | null {
  for (const c of cls.split(/\s+/)) { if (ROLE_OVERRIDE[c]) return ROLE_OVERRIDE[c]!; const m = c.match(ROLE_PREFIX); if (m && SUFFIX_ROLE[m[2]!]) return SUFFIX_ROLE[m[2]!]!; }
  return null;
}
type RoleStyle = { size: number; weight: number; track: string; family: '--font-ui' | '--font-condensed' };
const ROLE_DEFAULT: Record<TypeRole, RoleStyle> = {
  title: { size: 48, weight: 800, track: '0', family: '--font-ui' },
  region: { size: 64, weight: 800, track: '0', family: '--font-ui' },   // the table's place under its logo (Meet / Flirt lobbies), owner 22.9.2026: bigger than a title, in the logo's light tube colour
  label: { size: 32, weight: 800, track: '.01em', family: '--font-ui' },
  button: { size: 32, weight: 800, track: '0', family: '--font-ui' },
  chip: { size: 28, weight: 700, track: '0', family: '--font-ui' },
  name: { size: 36, weight: 800, track: '0', family: '--font-ui' },
  body: { size: 30, weight: 500, track: '0', family: '--font-ui' },
  caption: { size: 26, weight: 500, track: '0', family: '--font-ui' },
  tag: { size: 22, weight: 800, track: '0', family: '--font-ui' },
  tablehead: { size: 28, weight: 700, track: '0', family: '--font-condensed' },
  number: { size: 44, weight: 800, track: '0', family: '--font-ui' },
  code: { size: 100, weight: 900, track: '.02em', family: '--font-ui' },
  timer: { size: 140, weight: 800, track: '0', family: '--font-ui' },
  eyebrow: { size: 26, weight: 600, track: '.18em', family: '--font-ui' },
  action: { size: 56, weight: 800, track: '0', family: '--font-ui' },     // the game's own face (owner 22.9.2026: the brush font is gone)
  hero: { size: 84, weight: 800, track: '0', family: '--font-ui' },
  command: { size: 36, weight: 700, track: '0', family: '--font-condensed' },
  tab: { size: 48, weight: 700, track: '0', family: '--font-condensed' },
  filter: { size: 36, weight: 700, track: '0', family: '--font-condensed' },
  section: { size: 36, weight: 700, track: '.01em', family: '--font-condensed' },
  detail: { size: 28, weight: 600, track: '0', family: '--font-condensed' },
  status: { size: 26, weight: 700, track: '0', family: '--font-condensed' },
};
export const TYPE_ROLES = Object.keys(ROLE_DEFAULT) as TypeRole[];
export function roleStyle(r: TypeRole): RoleStyle { return ROLE_DEFAULT[r]; }
/** the tuner calls this after changing a token: every Ink measures again */

const gradCss = (g: NonNullable<InkProps['gradient']>): string => {
  const stops = (Array.isArray(g[0]) ? g as [number, string][] : [[0, g[0]], [1, g[1]]] as [number, string][]);
  return `linear-gradient(180deg, ${stops.map(([o, c]) => `${c} ${(o * 100).toFixed(1)}%`).join(', ')})`;
};

/**
 * A word in a CSS lane (OD-036, Marko 22.9.2026: "IKINÄ EI MITÄÄN JS VIRITYKSIÄ, PUHDASTA HTML CSS UI:ta"). Nothing here
 * measures, fits or rewrites a word at run time: the lane is a box (left/top/width/height in stage units), the size
 * and face come from the role's CSS class (`.ink.t-<role>` in app.css, one fixed size per role) or — for a class
 * without a role — from the reference cap height as a plain ratio, the word is centred by `text-align` and
 * `line-height`, and a word too long for its lane ends in an ellipsis by CSS (`ellipsis`) or simply overflows where
 * the owner can see and fix the lane. Move a lane in devtools and it holds.
 */
const CAP = 0.72;   // cap height per px of font size for the game's faces (Nunito / Barlow Condensed); a ratio, not a measurement
export function Ink({ box, text, class: cls = '', parent, align = 'left', maxW, size, z = 60, testid, ellipsis }: InkProps) {
  const role = roleOf(cls); const t = text ?? box.text;
  const ox = parent?.x ?? 0, oy = parent?.y ?? 0;
  const px = role ? ROLE_DEFAULT[role].size : (size ?? (box.cap ? box.cap / CAP : box.h));
  const lh = Math.round(px * 1.25);
  // the band the word is centred in: a role centres its cap in the lane box, a cap box is the cap itself, else the box
  const cy = box.cap && !role ? box.y + box.cap / 2 : box.y + box.h / 2;
  const w = role ? Math.min(maxW ?? box.w, box.w) : (maxW ?? box.w);
  const x = align === 'center' ? box.x + box.w / 2 - w / 2 : box.x;
  return (
    <div class={`ink ${cls}${role ? ` t-${role}` : ''}${ellipsis ? ' ellipsis' : ''}`} data-role={role ?? undefined}
      style={{ left: x - ox, top: cy - lh / 2 - oy, width: w, height: lh, lineHeight: `${lh}px`, textAlign: align, zIndex: z, ...(role ? {} : { fontSize: `${px}px` }) }}
      data-testid={testid} aria-hidden="true">
      <span>{t}</span>
    </div>
  );
}
